import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  apiGetPublicRiderShare,
  apiValidateRiderShare,
  apiUnlockRiderShare,
  type RiderShareDTO,
  type RiderShareItem,
} from '../api';
import PasswordInput from '../components/PasswordInput';
import PageHeader from '../components/PageHeader';

// Display order + human titles for the request sections shown to the promoter.
// Mirror the tech-rider: IEM, Backline, Sound, Lights, Hospitality.
const SECTION_ORDER = ['iem', 'backline', 'sound', 'lights', 'hospitality', 'stageDesign', 'wireless'];
const SECTION_TITLE_KEYS: Record<string, string> = {
  iem: 'shareRider.sectionIem',
  backline: 'shareRider.sectionBackline',
  sound: 'shareRider.sectionSound',
  lights: 'shareRider.sectionLights',
  hospitality: 'shareRider.sectionHospitality',
  stageDesign: 'shareRider.sectionStageDesign',
  wireless: 'shareRider.sectionWireless',
};
// Items in the DETAIL view (read-only, collapsed) but not the main confirmation
// grid. Anything not in a known section falls back here.
const UNKNOWN_SECTION = 'other';

/**
 * PUBLIC page reached from a shared rider link (/share/:token) — no login, no
 * session. The promoter sees a clear, grouped summary of what the band requests
 * (IEM units, backline, sound, lights, hospitality) and ticks each item to
 * confirm it is provided, adding a remark when needed. Every change is saved
 * online via the public validate route. The technical input list is NOT shown
 * here — the promoter confirms requests, not channels.
 */
export default function ShareRiderPage() {
  const { t } = useTranslation();
  const { token = '' } = useParams();
  const [data, setData] = useState<RiderShareDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Password unlock state for protected shares.
  const [pw, setPw] = useState('');
  const [pwBusy, setPwBusy] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);

  // Local working copy of checks + remarks, keyed by itemKey.
  const [edits, setEdits] = useState<Record<string, { confirmed: boolean; remarks: string }>>({});
  const [savingKeys, setSavingKeys] = useState<Set<string>>(new Set());
  const [saveState, setSaveState] = useState<{ ok: boolean; msg: string } | null>(null);

  useEffect(() => {
    if (!token) { setError(t('shareRider.missingToken')); setLoading(false); return; }
    let cancelled = false;
    // OPTION A (2026-10-24, user): ONE shared page. When the show has a tech/all
    // advancing link, this page REDIRECTS to /share/advancing/:advToken — the
    // unified page embeds the full rider AND stores validations in one place.
    // No advancing link (or any lookup error): this page renders as before
    // (read-only compat for old URLs).
    fetch(`/api/share/rider/${encodeURIComponent(token)}/advancing-token`)
      .then(async (r) => {
        if (!r.ok) throw new Error('no-redirect');
        const j = await r.json();
        if (!cancelled && j?.token) window.location.replace(`/share/advancing/${encodeURIComponent(j.token)}`);
      })
      .catch(() => { /* stay on this page */ });
    apiGetPublicRiderShare(token)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e?.message || t('shareRider.invalidOrDisabled')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  const unlock = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pw) return;
    setPwBusy(true); setPwError(null);
    try {
      await apiUnlockRiderShare(token, pw);
      // Cookie is set; re-fetch to load the full data.
      const d = await apiGetPublicRiderShare(token);
      setData(d);
    } catch (err) {
      setPwError(err instanceof Error ? err.message : t('shareRider.wrongPassword'));
    } finally {
      setPwBusy(false);
    }
  };

  const items = useMemo(() => (data ? data.items : []), [data]);

  const confirmedOf = useCallback((key: string): boolean => {
    if (key in edits) return edits[key].confirmed;
    const it = items.find((i) => i.itemKey === key);
    return !!it?.confirmed;
  }, [edits, items]);

  const remarksOf = useCallback((key: string): string => {
    if (key in edits) return edits[key].remarks;
    const it = items.find((i) => i.itemKey === key);
    return it?.remarks ?? '';
  }, [edits, items]);

  async function save(key: string, confirmed: boolean, remarks: string) {
    setSavingKeys((prev) => new Set(prev).add(key));
    setSaveState(null);
    try {
      await apiValidateRiderShare(token, key, confirmed, remarks, data?.transport);
      setSaveState({ ok: true, msg: t('shareRider.saved') });
      setData((prev) => prev ? { ...prev, items: prev.items.map((i) => i.itemKey === key ? { ...i, confirmed: confirmed ? 1 : 0, remarks } : i) } : prev);
    } catch (e) {
      setSaveState({ ok: false, msg: e instanceof Error ? e.message : t('shareRider.saveError') });
    } finally {
      setSavingKeys((prev) => { const n = new Set(prev); n.delete(key); return n; });
    }
  }

  const onToggle = (key: string, now: boolean) => {
    setEdits((prev) => ({ ...prev, [key]: { confirmed: now, remarks: remarksOf(key) } }));
    void save(key, now, remarksOf(key));
  };

  const onRemarks = (key: string, value: string) => {
    setEdits((prev) => ({ ...prev, [key]: { confirmed: confirmedOf(key), remarks: value } }));
  };

  const onRemarksBlur = (key: string) => {
    void save(key, confirmedOf(key), remarksOf(key));
  };

  // Split items: the promoter only confirms what the VENUE provides. Anything
  // the band provides itself (provider === 'band') is shown as info-only in a
  // "Provided by the band" section at the bottom, without a checkbox.
  const { venueItems, bandItems } = useMemo(() => {
    const venue: RiderShareItem[] = [];
    const band: RiderShareItem[] = [];
    for (const it of items) (it.provider === 'band' ? band : venue).push(it);
    return { venueItems: venue, bandItems: band };
  }, [items]);

  // Group the venue-provided (confirmable) items by their section.
  const bySection = useMemo(() => {
    const map = new Map<string, RiderShareItem[]>();
    for (const it of venueItems) {
      const sec = it.section && SECTION_ORDER.includes(it.section) ? it.section : UNKNOWN_SECTION;
      if (!map.has(sec)) map.set(sec, []);
      map.get(sec)!.push(it);
    }
    const ordered: Array<{ key: string; titleKey: string; items: RiderShareItem[] }> = [];
    for (const sec of SECTION_ORDER) {
      if (map.has(sec)) ordered.push({ key: sec, titleKey: SECTION_TITLE_KEYS[sec] || sec, items: map.get(sec)! });
    }
    if (map.has(UNKNOWN_SECTION)) ordered.push({ key: UNKNOWN_SECTION, titleKey: 'shareRider.sectionOther', items: map.get(UNKNOWN_SECTION)! });
    return ordered;
  }, [venueItems]);
  const confirmedCount = venueItems.filter((i) => !!i.confirmed).length;

  if (loading) return <div className="page-pad">{t('shareRider.loading')}</div>;

  if (error || !data) {
    return (
      <div className="page-pad" style={{ maxWidth: 640, margin: '60px auto' }}>
        <div className="card" style={{ padding: 28, textAlign: 'center' }}>
          <h2 style={{ marginTop: 0 }}>{t('shareRider.unavailable')}</h2>
          <p style={{ color: 'var(--text-dim)' }}>{error ?? t('shareRider.invalidLink')}</p>
          <p>{t('shareRider.noLogin')}</p>
        </div>
      </div>
    );
  }

  // Password-protected share that hasn't been unlocked yet — show a lock screen.
  if (data.locked) {
    const bandName = data.band?.name || '';
    return (
      <div className="page-pad" style={{ maxWidth: 480, margin: '60px auto' }}>
        <div className="card" style={{ padding: 28 }}>
          <h2 style={{ marginTop: 0, textAlign: 'center' }}>{bandName} — Tech Rider</h2>
          <p style={{ color: 'var(--text-dim)', textAlign: 'center', marginBottom: 18 }}>
            {data.showName ? t('shareRider.passwordProtectedShow', { show: data.showName }) : t('shareRider.passwordProtected')}
          </p>
          <form onSubmit={unlock}>
            <PasswordInput
              className="form-input"
              autoFocus
              value={pw}
              placeholder={t('shareRider.passwordPh')}
              onChange={(e) => setPw(e.target.value)}
              containerStyle={{ marginBottom: 12 }}
            />
            {pwError && <p style={{ color: 'var(--danger)', fontSize: '0.85rem', margin: '0 0 12px', textAlign: 'center' }}>{pwError}</p>}
            <button className="btn btn-primary" type="submit" disabled={pwBusy || !pw} style={{ width: '100%' }}>
              {pwBusy ? t('shareRider.unlocking') : t('shareRider.unlock')}
            </button>
          </form>
          <p style={{ color: 'var(--text-dim)', textAlign: 'center', fontSize: '0.85rem', marginBottom: 0, marginTop: 16 }}>
            {t('shareRider.publicPage')}<Link to="/" style={{ textDecoration: 'underline' }}>Bandz</Link>
          </p>
        </div>
      </div>
    );
  }

  const transportLabel = data.transport === 'plane' ? t('shareRider.transportPlane') : data.transport === 'tourbus' ? t('shareRider.transportTourbus') : data.transport === 'train' ? t('shareRider.transportTrain') : t('shareRider.transportVan');

  return (
    <div className="page rider-page">
      <PageHeader
        title={`${data.band.name} — Tech Rider`}
        actions={
          <>
            <span className="badge badge-muted">{transportLabel}</span>
            {saveState && (
              <span style={{ color: saveState.ok ? 'var(--success)' : 'var(--danger)', fontSize: '0.85rem' }}>
                {saveState.msg}
              </span>
            )}
          </>
        }
      />

      <p style={{ color: 'var(--text-dim)', marginBottom: 18 }}>
        {data.showName ? t('shareRider.validationSheet', { show: data.showName }) : ''}
        {t('shareRider.validationHint')}
      </p>

      {/* Summary bar */}
      <div className="card" style={{ padding: 12, marginBottom: 18, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 600 }}>
          {t('shareRider.providedCount', { confirmed: confirmedCount, total: venueItems.length })}
        </span>
        {confirmedCount === venueItems.length && venueItems.length > 0 && (
          <span style={{ color: 'var(--success, #2e7d32)', fontWeight: 600 }}>{t('shareRider.everythingConfirmed')}</span>
        )}
      </div>

      {/* Request sections (grouped) — what the venue must confirm */}
      {bySection.length === 0 && <p className="empty-hint">{t('shareRider.nothingToValidate')}</p>}
      {bySection.map((group) => (
        <div className="card" style={{ padding: 20, marginBottom: 18 }} key={group.key}>
          <h2 style={{ marginTop: 0, marginBottom: 12, borderBottom: '1px solid var(--divider)', paddingBottom: 6 }}>
            {t(group.titleKey)}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {group.items.map((it) => (
              <ItemRow
                key={it.itemKey}
                item={it}
                confirmed={confirmedOf(it.itemKey)}
                remarks={remarksOf(it.itemKey)}
                saving={savingKeys.has(it.itemKey)}
                onToggle={(v) => onToggle(it.itemKey, v)}
                onRemarks={(v) => onRemarks(it.itemKey, v)}
                onRemarksBlur={() => onRemarksBlur(it.itemKey)}
              />
            ))}
          </div>
        </div>
      ))}

      {/* What the band provides itself — info only, nothing for the promoter to confirm */}
      {bandItems.length > 0 && (
        <div className="card" style={{ padding: 20, marginBottom: 18 }}>
          <h2 style={{ marginTop: 0, marginBottom: 6, borderBottom: '1px solid var(--divider)', paddingBottom: 6 }}>
            {t('shareRider.providedByBand')}
          </h2>
          <p style={{ color: 'var(--text-dim)', fontSize: 'var(--font-sm)', margin: '0 0 10px' }}>
            {t('shareRider.providedByBandHint')}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {bandItems.map((it) => (
              <div key={it.itemKey} style={{
                padding: '8px 10px', border: '1px dashed var(--border)', borderRadius: 8,
                display: 'flex', alignItems: 'center', gap: 10,
              }}>
                <span style={{ opacity: 0.5 }}>✓</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontWeight: 600, fontSize: 'var(--font-md)' }}>{it.itemLabel}</span>
                  {it.detail && <div style={{ fontSize: 'var(--font-sm)', color: 'var(--text-dim)', marginTop: 2 }}>{it.detail}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <p style={{ color: 'var(--text-dim)', textAlign: 'center', fontSize: '0.85rem' }}>
        {t('shareRider.publicPage')}<Link to="/" style={{ textDecoration: 'underline' }}>Bandz</Link>
      </p>
    </div>
  );
}

function ItemRow({ item, confirmed, remarks, saving, onToggle, onRemarks, onRemarksBlur }: {
  item: RiderShareItem;
  confirmed: boolean;
  remarks: string;
  saving: boolean;
  onToggle: (v: boolean) => void;
  onRemarks: (v: string) => void;
  onRemarksBlur: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '8px 10px',
      border: '1px solid var(--border)', borderRadius: 8,
      background: confirmed ? 'rgba(0,212,126,.06)' : 'transparent',
    }}>
      <input
        type="checkbox"
        checked={confirmed}
        disabled={saving}
        style={{ transform: 'scale(1.3)', cursor: 'pointer', flexShrink: 0 }}
        onChange={(e) => onToggle(e.target.checked)}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, fontSize: 'var(--font-md)' }}>{item.itemLabel}</span>
          {item.quantity && item.quantity > 1 && (
            <span style={{ fontSize: 'var(--font-xs)', color: 'var(--text-dim)' }}>{t('shareRider.qty', { count: item.quantity })}</span>
          )}
          {item.quantity_changed && (
            <span style={{
              fontSize: 'var(--font-xs)', fontWeight: 600, color: 'var(--warning)',
              background: 'color-mix(in srgb, var(--warning) 15%, transparent)', padding: '1px 6px', borderRadius: 999,
            }} title={t('shareRider.reConfirmTitle')}>
              {t('shareRider.reConfirm')}
            </span>
          )}
          {item.provider === 'band' && (
            <span style={{ fontSize: 'var(--font-xs)', color: 'var(--text-dim)' }}>{t('shareRider.providedByBandTag')}</span>
          )}
        </div>
        {item.detail && <div style={{ fontSize: 'var(--font-sm)', color: 'var(--text-dim)', marginTop: 2 }}>{item.detail}</div>}
      </div>
      <input
        className="form-input"
        value={remarks}
        placeholder={t('shareRider.remarkPh')}
        style={{ width: 220, flexShrink: 0 }}
        onChange={(e) => onRemarks(e.target.value)}
        onBlur={onRemarksBlur}
      />
    </div>
  );
}
